import React, { useMemo, useState } from "react";
import { LayoutGrid, Library, List, Plus } from "lucide-react";
import { useQueries } from "@tanstack/react-query";

import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { BrandButton, GhostButton, PageHeader, SearchInput } from "@/shared/components/ui-kit";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { StatTile } from "@/shared/components/StatTile";
import { ApiError } from "@/shared/api/errors";
import { knowledgeKeys, listKbAgents, useKnowledgeBases } from "@/shared/api/knowledge";
import { DeleteKbDialog, KbFormDialog } from "./KbDialogs";
import { BASE_GRID, KbCard, KbListRow } from "./KbCard";
import { compact, formatSize, plural } from "./format";

import type { KnowledgeBase } from "@/shared/types";

type Dialog = { kind: "create" } | { kind: "edit"; base: KnowledgeBase } | { kind: "delete"; base: KnowledgeBase };
type Layout = "grid" | "list";

const LAYOUT_KEY = "knowledge-layout";
const readLayout = (): Layout => {
  try { return localStorage.getItem(LAYOUT_KEY) === "list" ? "list" : "grid"; } catch { return "grid"; }
};
/* The storage bar's shades, biggest base first; the rest share the last one. */
const SHADES = ["var(--text-primary)", "var(--text-tertiary)", "var(--text-quaternary)", "var(--hairline-strong)"];

/** Storage per base as one bar: each base its share, the small ones folded into "Other". */
function StorageBar({ bases, total }: { bases: KnowledgeBase[]; total: number }) {
  const sorted = [...bases].sort((a, b) => b.bytes - a.bytes);
  const shown: { name: string; bytes: number }[] = sorted.slice(0, SHADES.length - 1);
  const rest = sorted.slice(SHADES.length - 1);
  if (rest.length) shown.push({ name: rest.length === 1 ? rest[0].name : `${rest.length} more`, bytes: rest.reduce((n, b) => n + b.bytes, 0) });
  return (
    <div className="col-span-2 md:col-span-4 rounded-[14px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] shadow-lift px-4 py-3.5 space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
        <span className="font-medium text-[var(--text-primary)]">Storage by base</span>
        <span className="text-[12px] text-[var(--text-tertiary)]">{formatSize(total)} across {plural(bases.length, "base")}</span>
      </div>
      <div role="img" aria-label={shown.map((s) => `${s.name} ${formatSize(s.bytes)}`).join(", ")} className="h-2 rounded-full bg-[var(--surface-2)] flex gap-[2px] overflow-hidden">
        {total > 0 && shown.map((s, i) => (
          <span key={s.name} className="h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${Math.max(1.2, (s.bytes / total) * 100)}%`, background: SHADES[i] }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px] text-[var(--text-secondary)]">
        {shown.map((s, i) => (
          <span key={s.name} className="min-w-0 inline-flex items-center gap-2">
            <span className="w-2 h-2 shrink-0 rounded-[3px]" style={{ background: SHADES[i] }} />
            <span className="truncate max-w-[200px]">{s.name}</span>
            <span className="font-mono text-[11.5px] tabular-nums text-[var(--text-tertiary)]">{formatSize(s.bytes)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* The Knowledge home: what the org's knowledge holds (bases, files, storage, tokens), then
   its bases as cards or rows. A base belongs to the org, so this lives in the workspace nav;
   which agents read it is shown per base and set from each agent's Settings. */
export default function KnowledgeListPage({ onOpen }: { onOpen: (kbId: string) => void }) {
  const { data, isPending, error, refetch } = useKnowledgeBases();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [query, setQuery] = useState("");
  const [layout, setLayout] = useState<Layout>(readLayout);
  const close = () => setDialog(null);

  const bases = useMemo(() => data ?? [], [data]);
  // The same queries the cards make, so this costs nothing extra.
  const links = useQueries({ queries: bases.map((b) => ({ queryKey: knowledgeKeys.agents(b.id), queryFn: () => listKbAgents(b.id) })) });
  const readers = new Set(links.flatMap((q) => (q.data ?? []).map((l) => l.agent_id))).size;
  const totals = bases.reduce((t, b) => ({ files: t.files + b.file_count, bytes: t.bytes + b.bytes, tokens: t.tokens + b.tokens }), { files: 0, bytes: 0, tokens: 0 });
  const q = query.trim().toLowerCase();
  const shown = q ? bases.filter((b) => `${b.name} ${b.description ?? ""}`.toLowerCase().includes(q)) : bases;

  const pickLayout = (next: Layout) => {
    setLayout(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch { /* a private window keeps it for this visit */ }
  };
  const actions = (base: KnowledgeBase) => ({
    base,
    onOpen: () => onOpen(base.id),
    onEdit: () => setDialog({ kind: "edit", base }),
    onDelete: () => setDialog({ kind: "delete", base }),
  });
  const create = (
    <BrandButton onClick={() => setDialog({ kind: "create" })} className="shrink-0">
      <Plus className="w-3.5 h-3.5" strokeWidth={2} />
      New<span className="hidden sm:inline"> knowledge base</span>
    </BrandButton>
  );

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      <PageHeader
        title={<Breadcrumb>Knowledge</Breadcrumb>}
      >
        {bases.length > 0 && (
          <div className="ml-auto flex min-w-0 items-center gap-2">
            <SearchInput aria-label="Search bases" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search bases" className="w-[220px] min-w-[120px] shrink" />
            {create}
          </div>
        )}
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 py-6 space-y-5 fade-in-up">
          {isPending ? (
            [0, 1, 2].map((i) => <Skeleton key={i} data-testid="kb-skeleton" className="h-[88px] rounded-[18px] bg-[var(--surface-2)]" />)
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-[14px] text-[var(--text-primary)] m-0">Could not load knowledge bases.</p>
              <p className="text-[12.5px] text-[var(--text-tertiary)] mt-1 mb-4">
                {error.message}
                {error instanceof ApiError && error.request_id && (
                  <span className="font-mono text-[var(--text-quaternary)]"> · ref {error.request_id}</span>
                )}
              </p>
              <GhostButton onClick={() => refetch()}>Retry</GhostButton>
            </div>
          ) : !bases.length ? (
            <div className="text-center py-16 tray-box">
              <Library className="w-5 h-5 mx-auto mb-2 text-[var(--text-quaternary)]" strokeWidth={1.5} />
              <p className="text-[14px] font-medium text-[var(--text-primary)] m-0">No knowledge bases yet</p>
              <p className="text-[12.5px] text-[var(--text-tertiary)] mt-1 mb-4 max-w-[360px] mx-auto">
                Group files into a base, then give it to the agents that should read it.
              </p>
              {create}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-1 rounded-[18px] bg-[var(--surface-2)]">
                <StatTile label="Knowledge bases" value={bases.length} sub={readers ? `read by ${plural(readers, "agent")}` : "no agent reads them yet"} />
                <StatTile label="Files" value={totals.files.toLocaleString("en-US")} sub={`across ${plural(bases.length, "base")}`} />
                <StatTile label="Storage" value={formatSize(totals.bytes)} sub={`${totals.bytes.toLocaleString("en-US")} bytes`} />
                <StatTile label="Tokens indexed" value={compact(totals.tokens)} sub="what agents can read" />
                <StorageBar bases={bases} total={totals.bytes} />
              </div>

              <section aria-label="Knowledge bases" className="rounded-[18px] bg-[var(--surface-2)] p-1">
                <div className="flex items-center gap-2 min-h-[40px] pl-3 pr-1 pb-1">
                  <span className="text-[12.5px] text-[var(--text-tertiary)]">{q ? `${shown.length} of ${plural(bases.length, "base")}` : plural(bases.length, "base")}</span>
                  <span className="flex-1" />
                  <div role="group" aria-label="Layout" className="inline-flex p-[3px] rounded-full bg-[var(--surface-3)]">
                    {([["grid", LayoutGrid, "Cards"], ["list", List, "List"]] as const).map(([value, Icon, label]) => (
                      <button
                        key={value}
                        type="button"
                        title={label}
                        aria-label={label}
                        aria-pressed={layout === value}
                        onClick={() => pickLayout(value)}
                        className={`press w-7 h-6 rounded-full flex items-center justify-center ${layout === value ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-lift" : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"}`}
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </button>
                    ))}
                  </div>
                </div>
                {!shown.length ? (
                  <p className="m-0 py-10 text-center text-[13px] text-[var(--text-tertiary)] rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)]">No base matches “{query.trim()}”.</p>
                ) : layout === "grid" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                    {shown.map((base) => <KbCard key={base.id} {...actions(base)} />)}
                  </div>
                ) : (
                  <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden">
                    <div className={`${BASE_GRID} px-3 py-2 border-b hairline text-[12px] text-[var(--text-tertiary)]`}>
                      <span>Name</span>
                      <span className="hidden md:block text-right">Files</span>
                      <span className="text-right">Storage</span>
                      <span className="hidden md:block">Used by</span>
                      <span />
                    </div>
                    <div className="divide-y divide-[var(--hairline)]">
                      {shown.map((base) => <KbListRow key={base.id} {...actions(base)} />)}
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>

      {dialog?.kind === "create" && <KbFormDialog onClose={close} />}
      {dialog?.kind === "edit" && <KbFormDialog base={dialog.base} onClose={close} />}
      {dialog?.kind === "delete" && <DeleteKbDialog base={dialog.base} onClose={close} />}
    </main>
  );
}
